import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  CompanySettingsSchema,
  CompanySurfaceTreatmentSchema,
  IndustrialProcessSchema,
  PartTypeSchema,
} from "../generated/prisma/zod";

const c = initContract();
export const companySettingsContract = c.router({
  update: {
    method: "PATCH",
    path: "/company-settings",
    body: CompanySettingsSchema.omit({
      id: true,
      createdAt: true,
      updatedAt: true,
      exportTemplateJson: true,
      partTypeMapperOverride: true,
      defaultSheetFormat: true,
    })
      .extend({
        defaultProducts: z.array(z.object({ id: z.string().uuid() })).optional(),
      })
      .extend({
        partTypeMapperOverride: z.record(PartTypeSchema, PartTypeSchema).nullable(),
      })
      .extend({
        defaultSheetFormat: z
          .object({
            width: z.number(),
            length: z.number(),
          })
          .nullable(),
      })
      .partial(),
    responses: {
      200: CompanySettingsSchema,
      400: z.object({ message: z.string() }),
    },
    summary: "Update a company",
  },
  updateModel: {
    method: "PUT",
    path: "/company-settings/:id",
    body: z.object({
      modelId: z.string().uuid(),
    }),
    responses: {
      200: CompanySettingsSchema,
    },
    summary: "Update the used model",
  },
  customSurfaceTreatments: {
    method: "GET",
    path: "/company-settings/:id/surface-treatments",
    responses: {
      200: CompanySurfaceTreatmentSchema.array(),
    },
  },
  createCustomSurfaceTreatment: {
    method: "POST",
    path: "/company-settings/:id/surface-treatments",
    body: CompanySurfaceTreatmentSchema.omit({
      id: true,
      createdAt: true,
      updatedAt: true,
      companySettingsId: true,
    }).extend({
      industrialProcess: IndustrialProcessSchema.nullable().optional(),
    }),
    responses: {
      201: CompanySurfaceTreatmentSchema,
    },
  },
  updateCustomSurfaceTreatment: {
    method: "PATCH",
    path: "/company-settings/:id/surface-treatments/:surfaceTreatmentId",
    pathParams: z.object({
      id: z.string().uuid(),
      surfaceTreatmentId: z.string().uuid(),
    }),
    body: CompanySurfaceTreatmentSchema.omit({
      id: true,
      createdAt: true,
      updatedAt: true,
      companySettingsId: true,
    }).extend({
      industrialProcess: IndustrialProcessSchema.nullable().optional(),
    }),
    responses: {
      200: CompanySurfaceTreatmentSchema,
      400: z.object({
        message: z.string(),
      }),
    },
    summary: "Update custom surface treatment for my company",
  },
  updateCustomSurfaceTreatments: {
    method: "PATCH",
    path: "/company-settings/:id/surface-treatments",
    body: CompanySurfaceTreatmentSchema.omit({
      id: true,
      createdAt: true,
      updatedAt: true,
      companySettingsId: true,
    })
      .extend({
        industrialProcess: IndustrialProcessSchema.nullable().optional(),
      })
      .array(),
    responses: {
      200: CompanySurfaceTreatmentSchema.array(),
      400: z.object({
        message: z.string(),
      }),
    },
    summary: "Update all custom surface treatments for my company",
  },
  deleteCustomSurfaceTreatment: {
    method: "DELETE",
    path: "/company-settings/:id/surface-treatments/:surfaceTreatmentId",
    pathParams: z.object({
      id: z.string().uuid(),
      surfaceTreatmentId: z.string().uuid(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: z.object({
        success: z.boolean(),
      }),
    },
    summary: "Delete custom surface treatment",
  },
  uploadCustomSurfaceTreatments: {
    method: "POST",
    path: "/company-settings/:id/surface-treatments/upload",
    contentType: "multipart/form-data",
    responses: {
      200: z.object({
        success: z.boolean(),
        message: z.string().optional(),
      }),
      400: z.object({
        message: z.string(),
        errors: z
          .array(
            z.object({
              row: z.number(),
              field: z.string().optional(),
              error: z.string(),
            }),
          )
          .optional(),
      }),
    },
    body: c.type<{ file: File }>(),
    summary: "Upload custom surface treatments CSV file",
  },
  importConfig: {
    method: "POST",
    path: "/company-settings/import-config",
    contentType: "multipart/form-data",
    body: c.type<{ file: File }>(),
    responses: {
      200: z.object({ success: z.boolean(), message: z.string() }),
      400: z.object({ message: z.string(), errors: z.array(z.string()).optional() }),
    },
    summary: "Import company configuration from JSON file",
  },
});
